"use client";

import { motion, AnimatePresence } from "motion/react";
import { useTranslation } from "react-i18next";
import { Ban, LogOut } from "lucide-react";

import { cn } from "@/lib/utils";

interface WorkspaceBlockingIndicatorProps {
  isBlocked: boolean;
  hasLeft: boolean;
  conversationType: "individual" | "group";
  className?: string;
}

export function WorkspaceBlockingIndicator({
  isBlocked,
  hasLeft,
  conversationType,
  className,
}: WorkspaceBlockingIndicatorProps) {
  const { t } = useTranslation(["chat", "common"]);

  // Don't show anything if user is not blocked and hasn't left
  if (!isBlocked && !hasLeft) {
    return null;
  }

  // For individual conversations, only show if blocked (left status doesn't matter)
  if (conversationType === "individual" && !isBlocked) {
    return null;
  }

  // For group conversations, show if blocked OR left
  if (conversationType === "group" && !isBlocked && !hasLeft) {
    return null;
  }

  const getBlockingInfo = () => {
    // Blocked status takes precedence over left status
    if (isBlocked) {
      return {
        color: "text-red-500",
        bgColor: "bg-red-500/10",
        borderColor: "border-red-500/30",
        icon: Ban,
        title: t("chat:workspaceBlocking.blocked" as any),
        description: t("chat:workspaceBlocking.blockedDescription" as any),
      };
    }

    // Only show left status for group conversations
    if (hasLeft && conversationType === "group") {
      return {
        color: "text-orange-500",
        bgColor: "bg-orange-500/10",
        borderColor: "border-orange-500/30",
        icon: LogOut,
        title: t("chat:workspaceBlocking.leftWorkspace" as any),
        description: t("chat:workspaceBlocking.leftDescription" as any),
        reason: t("chat:workspaceBlocking.leftReason" as any),
      };
    }

    return null;
  };

  const blockingInfo = getBlockingInfo();

  if (!blockingInfo) {
    return null;
  }

  const Icon = blockingInfo.icon;

  return (
    <AnimatePresence>
      <motion.div
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className={cn(
          "flex items-center gap-2 rounded-lg border px-3 py-2",
          blockingInfo.bgColor,
          blockingInfo.borderColor,
          className,
        )}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        transition={{ duration: 0.2, type: "spring", stiffness: 400, damping: 25 }}
      >
        <Icon className={cn("h-4 w-4", blockingInfo.color)} />

        <div className="flex-1">
          <div className={cn("text-sm font-medium", blockingInfo.color)}>{blockingInfo.title}</div>
          <div className="text-muted-foreground text-xs">{blockingInfo.description}</div>
          {blockingInfo.reason && (
            <div className="text-muted-foreground mt-1 text-xs">{blockingInfo.reason}</div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
